import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Download, FileCode, Filter, Info, ChevronRight, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const MODULO_COLORS = {
  studio_tecnico: 'bg-blue-100 text-blue-800',
  haccp: 'bg-orange-100 text-orange-800',
};

const REGIONI_ITALIA = [
  'Abruzzo','Basilicata','Calabria','Campania','Emilia-Romagna','Friuli-Venezia Giulia',
  'Lazio','Liguria','Lombardia','Marche','Molise','Piemonte','Puglia','Sardegna',
  'Sicilia','Toscana','Trentino-Alto Adige','Umbria','Valle d\'Aosta','Veneto'
];

const emptyForm = {
  nome_visualizzato: '',
  tipo_documento: '',
  modulo: 'studio_tecnico',
  regione: '',
  provincia: '',
  comune: '',
  note: '',
  file: null,
};

export default function ModulisticaPratichePage() {
  const [items, setItems] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [moduli, setModuli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [openGenerate, setOpenGenerate] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [placeholders, setPlaceholders] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [genCommessaId, setGenCommessaId] = useState('');
  const [genClienteId, setGenClienteId] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterModulo, setFilterModulo] = useState('all');
  const [filterRegione, setFilterRegione] = useState('all');

  const load = async () => {
    try {
      setLoading(true);
      const [tplR, tipiR, comR, cliR] = await Promise.all([
        studioApi.getTemplates(),
        studioApi.getTemplateTipi(),
        studioApi.getCommesse(),
        studioApi.getClienti(),
      ]);
      setItems(tplR.data || []);
      setTipi(tipiR.data?.tipi_documento || []);
      setModuli(tipiR.data?.moduli || []);
      setCommesse(comR.data || []);
      setClienti(cliR.data || []);
    } catch (e) {
      toast.error('Errore caricamento template');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openInfoPanel = async () => {
    try {
      const r = await studioApi.getTemplatePlaceholders();
      setPlaceholders(r.data);
      setOpenInfo(true);
    } catch {
      toast.error('Errore caricamento placeholder');
    }
  };

  const submitUpload = async () => {
    if (!form.file) { toast.error('Seleziona un file .docx'); return; }
    if (!form.nome_visualizzato) { toast.error('Nome obbligatorio'); return; }
    if (!form.tipo_documento) { toast.error('Tipo documento obbligatorio'); return; }
    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('nome_visualizzato', form.nome_visualizzato);
    fd.append('tipo_documento', form.tipo_documento);
    fd.append('modulo', form.modulo);
    fd.append('regione', form.regione || '');
    fd.append('provincia', form.provincia || '');
    fd.append('comune', form.comune || '');
    fd.append('note', form.note || '');
    try {
      const r = await studioApi.uploadTemplate(fd);
      toast.success(`Template caricato — placeholder rilevati: ${r.data.placeholders?.length || 0}`);
      setOpenUpload(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Cancellare definitivamente questo template?')) return;
    try {
      await studioApi.deleteTemplate(id);
      toast.success('Template cancellato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const downloadOriginal = async (tpl) => {
    try {
      const r = await studioApi.downloadTemplateOriginal(tpl.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = tpl.original_filename || `${tpl.nome_visualizzato}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };

  const openGenDialog = (tpl) => {
    setSelectedTpl(tpl);
    setGenCommessaId('');
    setGenClienteId('');
    setOpenGenerate(true);
  };

  const doGenerate = async () => {
    if (!genCommessaId && !genClienteId) {
      toast.error('Seleziona una commessa o un cliente'); return;
    }
    try {
      const payload = {};
      if (genCommessaId) payload.commessa_id = genCommessaId;
      if (genClienteId) payload.cliente_id = genClienteId;
      const r = await studioApi.generateTemplate(selectedTpl.id, payload);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const safeNome = (selectedTpl.tipo_documento || 'Documento').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `${safeNome}_${new Date().toISOString().slice(0,10)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Documento generato e scaricato');
      setOpenGenerate(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione');
    }
  };

  const filtered = items.filter(t => {
    if (filterTipo !== 'all' && t.tipo_documento !== filterTipo) return false;
    if (filterModulo !== 'all' && t.modulo !== filterModulo) return false;
    if (filterRegione !== 'all' && t.regione !== filterRegione) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="modulistica-pratiche-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><FileText className="w-7 h-7 text-blue-600" /> Modulistica Pratiche</h1>
          <p className="text-sm text-slate-600 mt-1">Template .docx con auto-compilazione (CILA, SCA, Direzione Lavori, Occupazione Suolo Pubblico, Dichiarazioni HACCP, ecc.)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openInfoPanel} data-testid="info-placeholders-btn">
            <Info className="w-4 h-4 mr-1" /> Placeholder disponibili
          </Button>
          <Button onClick={() => { setForm(emptyForm); setOpenUpload(true); }} data-testid="upload-template-btn">
            <Upload className="w-4 h-4 mr-1" /> Carica template
          </Button>
        </div>
      </motion.div>

      {/* Filtri */}
      <div className="flex flex-wrap items-end gap-3 bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs text-slate-600">Modulo</Label>
          <Select value={filterModulo} onValueChange={setFilterModulo}>
            <SelectTrigger data-testid="filter-modulo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {moduli.map(m => <SelectItem key={m} value={m}>{m === 'studio_tecnico' ? 'Studio Tecnico' : 'HACCP'}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-600">Tipo documento</Label>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger data-testid="filter-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {tipi.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs text-slate-600">Regione</Label>
          <Select value={filterRegione} onValueChange={setFilterRegione}>
            <SelectTrigger data-testid="filter-regione"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {REGIONI_ITALIA.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Badge variant="secondary" className="self-center">{filtered.length} / {items.length} template</Badge>
      </div>

      {/* Lista */}
      {loading ? <p className="text-slate-500">Caricamento...</p> : filtered.length === 0 ? (
        <div className="bg-white rounded-lg p-10 text-center border" data-testid="empty-state">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">Nessun template caricato. Carica il primo modello .docx per iniziare.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => (
            <motion.div key={t.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white p-4 rounded-lg shadow-sm border flex flex-col" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">{t.nome_visualizzato}</h3>
                  <p className="text-xs text-slate-500 mt-1">{t.tipo_documento}</p>
                </div>
                <Badge className={MODULO_COLORS[t.modulo] || ''}>{t.modulo === 'studio_tecnico' ? 'Studio' : 'HACCP'}</Badge>
              </div>
              <div className="flex flex-wrap gap-1 my-2">
                {t.regione && <Badge variant="outline" className="text-xs">{t.regione}</Badge>}
                {t.provincia && <Badge variant="outline" className="text-xs">{t.provincia}</Badge>}
                {t.comune && <Badge variant="outline" className="text-xs">{t.comune}</Badge>}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                <Tag className="w-3 h-3" /> {t.placeholders?.length || 0} placeholder
              </div>
              {t.note && <p className="text-xs text-slate-600 italic mb-3 line-clamp-2">{t.note}</p>}
              <div className="flex gap-1 mt-auto pt-2 border-t flex-wrap">
                <Button size="sm" onClick={() => openGenDialog(t)} data-testid={`generate-btn-${t.id}`}>
                  <FileCode className="w-3.5 h-3.5 mr-1" /> Genera
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadOriginal(t)} title="Scarica template originale" data-testid={`download-template-btn-${t.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del(t.id)} className="text-red-600 hover:bg-red-50 ml-auto" data-testid={`delete-btn-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={openUpload} onOpenChange={setOpenUpload}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Carica nuovo template</DialogTitle>
            <DialogDescription>I placeholder Jinja2 ({'{{cliente.nome}}'}) verranno rilevati automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome visualizzato *</Label>
              <Input value={form.nome_visualizzato} onChange={e => setForm({...form, nome_visualizzato: e.target.value})} placeholder="es. CILA Comune di Palermo" data-testid="form-nome" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo documento *</Label>
                <Select value={form.tipo_documento} onValueChange={v => setForm({...form, tipo_documento: v})}>
                  <SelectTrigger data-testid="form-tipo"><SelectValue placeholder="Scegli..." /></SelectTrigger>
                  <SelectContent>{tipi.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Modulo</Label>
                <Select value={form.modulo} onValueChange={v => setForm({...form, modulo: v})}>
                  <SelectTrigger data-testid="form-modulo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {moduli.map(m => <SelectItem key={m} value={m}>{m === 'studio_tecnico' ? 'Studio Tecnico' : 'HACCP'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Regione</Label>
                <Select value={form.regione} onValueChange={v => setForm({...form, regione: v})}>
                  <SelectTrigger data-testid="form-regione"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {REGIONI_ITALIA.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Provincia</Label>
                <Input value={form.provincia} onChange={e => setForm({...form, provincia: e.target.value})} placeholder="es. PA" maxLength={2} data-testid="form-provincia" />
              </div>
              <div>
                <Label>Comune</Label>
                <Input value={form.comune} onChange={e => setForm({...form, comune: e.target.value})} placeholder="es. Palermo" data-testid="form-comune" />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})} rows={2} placeholder="Promemoria interno, normativa, versione modulo..." data-testid="form-note" />
            </div>
            <div>
              <Label>File .docx *</Label>
              <Input type="file" accept=".docx" onChange={e => setForm({...form, file: e.target.files[0]})} data-testid="form-file" />
              <p className="text-xs text-slate-500 mt-1">Max 10 MB. Usa placeholder Jinja2 nel testo, es. {'{{cliente.denominazione}}'}.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUpload(false)}>Annulla</Button>
            <Button onClick={submitUpload} data-testid="submit-upload-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={openGenerate} onOpenChange={setOpenGenerate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Genera documento</DialogTitle>
            <DialogDescription>Scegli una commessa (consigliato) o un cliente per popolare i dati.</DialogDescription>
          </DialogHeader>
          {selectedTpl && (
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded text-sm">
                <strong>{selectedTpl.nome_visualizzato}</strong>
                <p className="text-xs text-slate-600 mt-1">{selectedTpl.tipo_documento} · {selectedTpl.placeholders?.length || 0} placeholder</p>
              </div>
              <div>
                <Label>Commessa</Label>
                <Select value={genCommessaId} onValueChange={(v) => { setGenCommessaId(v); setGenClienteId(''); }}>
                  <SelectTrigger data-testid="gen-commessa"><SelectValue placeholder="— Nessuna —" /></SelectTrigger>
                  <SelectContent>
                    {commesse.map(c => <SelectItem key={c.id} value={c.id}>{c.codice} — {c.oggetto?.slice(0, 50)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-center text-xs text-slate-400">— oppure —</div>
              <div>
                <Label>Cliente (senza commessa)</Label>
                <Select value={genClienteId} onValueChange={(v) => { setGenClienteId(v); setGenCommessaId(''); }}>
                  <SelectTrigger data-testid="gen-cliente"><SelectValue placeholder="— Nessuno —" /></SelectTrigger>
                  <SelectContent>
                    {clienti.map(c => <SelectItem key={c.id} value={c.id}>{c.nome || c.denominazione}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenGenerate(false)}>Annulla</Button>
            <Button onClick={doGenerate} data-testid="confirm-generate-btn"><FileCode className="w-4 h-4 mr-1" /> Genera .docx</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Placeholders Dialog */}
      <Dialog open={openInfo} onOpenChange={setOpenInfo}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Placeholder disponibili</DialogTitle>
            <DialogDescription>Nei file .docx scrivi questi tag e verranno sostituiti automaticamente al momento della generazione.</DialogDescription>
          </DialogHeader>
          {placeholders && (
            <div className="space-y-4">
              <p className="text-sm bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
                <strong>Esempio:</strong> <code className="text-xs">{placeholders.esempio_uso}</code>
              </p>
              {Object.entries(placeholders.categorie || {}).map(([cat, vars]) => (
                <div key={cat}>
                  <h4 className="font-semibold text-sm text-slate-700 uppercase mb-2 flex items-center gap-1">
                    <ChevronRight className="w-4 h-4" /> {cat}
                  </h4>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {vars.map(v => (
                      <code key={v} className="bg-slate-100 px-2 py-1 rounded">{`{{${v}}}`}</code>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
